/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import TabContext from '@mui/lab/TabContext'
import { useLocation } from 'react-router-dom'
import { MainNav } from './MainNav'
import { Drawer } from '../drawer/Drawer'
import { Outlet } from 'react-router-dom'
import { BackofficeTheme } from '../backoffice/BackofficeTheme'
import { mono, boFont, boSize, boMobile } from '../backoffice/backoffice.tokens'
import { useBoSizeClass } from '../backoffice/useBoSizeClass'
import { BoShellContext, BoShellContextType } from '../backoffice/BoShellContext'

import { useAppSelector } from '@redux/index'

/**
 * 백오피스 프레임 — 좌 사이드바(Drawer) · 상단 탭 바(MainNav) · 콘텐츠.
 * (2026-09-27 반응형: 폭 등급으로 쉘 구조를 고른다. 데스크톱은 그대로다.)
 * 2026-09-16 Monolith 전환: 워터마크 로고·홈 전용 noPadding 분기를 걷어냈다. 페이지는 boWrap 안에서 자기 여백을 잡는다.
 *
 * 폭 등급 (RESPONSIVE_PLAN §4):
 *   데스크톱 ≥1200 = 지금 그대로(permanent 240/64)
 *   태블릿 600~1199 = 레일 64 고정 + 상단 바 ≡ 로 임시 드로어
 *   폰 <600 = 사이드바 없음, 상단 바(≡ · 제목), 임시 드로어 (하단 탭 바는 2026-09-27 사용자 결정으로 없앴다)
 */
export const MainLayout = (): JSX.Element => {
	// redux state
	const selectedTabIdx: string = useAppSelector(state => state.config.selectedTabIdx)
	const drawerWidth: number = useAppSelector(state => state.config.drawerWidth)
	const closedDrawerWidth: number = useAppSelector(
		state => state.config.closedDrawerWidth,
	)
	const isOpenDrawer: boolean = useAppSelector(state => state.config.isOpenDrawer)

	const sizeClass = useBoSizeClass()
	const location = useLocation()
	const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false)

	// 경로가 바뀌면 임시 드로어는 닫힌다
	React.useEffect(() => {
		setMobileDrawerOpen(false)
	}, [location.pathname])

	const shell: BoShellContextType = React.useMemo(
		() => ({
			sizeClass,
			drawerOpen: null,
			mobileDrawerOpen,
			setMobileDrawerOpen,
		}),
		[sizeClass, mobileDrawerOpen],
	)

	// variables
	const sideWidth: number =
		sizeClass === 'phone'
			? 0
			: sizeClass === 'tablet'
			? boMobile.railW
			: isOpenDrawer
			? drawerWidth
			: closedDrawerWidth

	return (
		<BackofficeTheme>
			<BoShellContext.Provider value={shell}>
				<View css={style} className={`boShell ${sizeClass}`}>
					{sizeClass === 'desktop' ? <Drawer mode="desktop" /> : null}
					{sizeClass === 'tablet' ? <Drawer mode="rail" /> : null}
					{sizeClass !== 'desktop' ? <Drawer mode="temporary" /> : null}

					<View
						className="mainLayoutInnerView"
						css={{ width: `calc(100% - ${sideWidth}px)` }}
					>
						<TabContext value={`Tab ${parseInt(selectedTabIdx)}`}>
							<MainNav sideWidth={sideWidth} />

							<View className="container">
								<View className="containerInnerView">
									<Outlet />
								</View>
							</View>
						</TabContext>
					</View>
				</View>
			</BoShellContext.Provider>
		</BackofficeTheme>
	)
}

const style = css({
	display: 'flex',
	position: 'relative',
	padding: 0,
	height: ['100vh', '100dvh'],
	width: '100%',
	fontFamily: boFont.ui,
	color: mono.ink,
	background: mono.bg,
	textAlign: 'left',

	/** Text 아톰은 자기 서체를 박아 둔다 — 백오피스 트리 안에서는 Archivo(+한글 폴백)로 */
	'& .text': { fontFamily: boFont.ui },

	'.mainLayoutInnerView': {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: '100%',
		transition: 'width 200ms ease-out',

		'.container': {
			zIndex: 3,
			flexGrow: 1,
			minHeight: 0,
			marginTop: boSize.navH,
			overflow: 'hidden',
		},

		'.containerInnerView': {
			position: 'relative',
			zIndex: 3,
			width: '100%',
			height: '100%',
			overflow: 'auto',
			overscrollBehaviorY: 'contain',
			overscrollBehaviorX: 'auto',
			WebkitOverflowScrolling: 'touch',
		},
	},

	// 폰 — 상단 바는 safe-area 만큼 내려온다
	'&.phone': {
		'.mainLayoutInnerView': {
			'.container': {
				marginTop: `calc(${boSize.navH}px + env(safe-area-inset-top, 0px))`,
				paddingBottom: 'env(safe-area-inset-bottom, 0px)',
				boxSizing: 'border-box',
			},
		},
	},

	/**
	 * 폰·태블릿 — 화면 밖으로 새는 것을 여기서 막는다 (2026-09-29 사용자 지적: 오른쪽이 잘린다).
	 * 세로 스크롤러는 `.containerInnerView` 하나고, 가로로는 아무것도 흐르지 않는다.
	 * 안쪽에서 넓은 것을 만들면 여기서 잘리므로, 페이지는 폭을 스스로 접어야 한다.
	 */
	'&.phone, &.tablet': {
		maxWidth: '100vw',
		overflowX: 'hidden',
		'.mainLayoutInnerView': {
			maxWidth: '100%',
			minWidth: 0,
			'.container': { maxWidth: '100%', minWidth: 0 },
			'.containerInnerView': { maxWidth: '100%', minWidth: 0, overflowX: 'hidden' },
		},
	},
})
